import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/utils/cn"

interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  /** Porta il focus sul trigger all’apertura (es. dialog modale che altrimenti focalizza il primo input) */
  autoFocusTrigger?: boolean;
  /** Notifica il testo di ricerca (es. ricerca API esterna) */
  onSearchChange?: (query: string) => void;
  /** Se true, non filtrare localmente le options */
  disableLocalFilter?: boolean;
  /** Etichetta mostrata sul trigger se diversa dall’opzione selezionata */
  displayLabel?: string;
  /** Consente di confermare il testo digitato come valore libero (Enter o riga in elenco) */
  onCustomValue?: (query: string) => void;
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Seleziona...",
  searchPlaceholder = "Cerca...",
  emptyMessage = "Nessun risultato trovato.",
  className,
  disabled,
  autoFocusTrigger,
  onSearchChange,
  disableLocalFilter,
  displayLabel,
  onCustomValue,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  const containerRef = React.useRef<HTMLDivElement>(null)
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  const selectedOption = options.find((option) => option.value === value)

  const filteredOptions = React.useMemo(() => {
    if (disableLocalFilter || !search) return options;
    const searchLower = search.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchLower) ||
      option.description?.toLowerCase().includes(searchLower)
    );
  }, [options, search, disableLocalFilter]);

  React.useEffect(() => {
    onSearchChange?.(search);
  }, [search, onSearchChange]);

  React.useEffect(() => {
    if (open && searchInputRef.current) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        ref={triggerRef}
        tabIndex={0}
        data-combobox-trigger
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
        onClick={() => !disabled && setOpen(!open)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!disabled) setOpen(!open);
          }
        }}
      >
        <span className={cn("truncate", !(displayLabel || selectedOption) && "text-muted-foreground")}>
          {displayLabel || selectedOption?.label || placeholder}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </div>

      {open && (
        <div className="absolute z-[9999] mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-lg">
          <div className="p-2 border-b">
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter' && filteredOptions.length === 1) {
                  onValueChange(filteredOptions[0].value);
                  setOpen(false);
                  setSearch("");
                } else if (e.key === 'Enter' && onCustomValue && search.trim()) {
                  onCustomValue(search.trim());
                  setOpen(false);
                  setSearch("");
                }
                if (e.key === 'Escape') {
                  setOpen(false);
                  setSearch("");
                }
              }}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
          <div className="max-h-[300px] overflow-auto p-1">
            {onCustomValue && search.trim() && (
              <div
                className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                onClick={() => {
                  onCustomValue(search.trim());
                  setOpen(false);
                  setSearch("");
                }}
              >
                Usa «{search.trim()}»
              </div>
            )}
            {filteredOptions.length === 0 && !(onCustomValue && search.trim()) ? (
              <div className="p-4 text-center text-sm text-gray-500">
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={cn(
                    "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                    value === option.value && "bg-accent text-accent-foreground"
                  )}
                  onClick={() => {
                    onValueChange(option.value);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{option.label}</div>
                    {option.description && (
                      <div className="text-xs text-muted-foreground truncate">{option.description}</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
